import { useEffect, useMemo } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { cn } from '@/shared/lib';
import type { RichTextDisplayProps } from '@/shared/ui/rich-text-editor/types';
import { richTextDisplayStyles, richTextProseStyles } from '@/shared/ui/rich-text-editor/styles';
import { createExtensions, normaliseHtml } from './schema';

/**
 * Renders stored HTML by parsing it through the editor's own schema.
 *
 * This is the sanitiser: a read-only Tiptap document can only contain nodes and
 * marks the schema declares, so a `<script>`, an `<iframe>` or an `onerror`
 * attribute in stored HTML has nowhere to land. No `dangerouslySetInnerHTML`, no
 * scrubber to keep in sync with the editor — one schema, both directions.
 */
export function RichTextDisplay({
  html,
  emptyPlaceholder = '—',
  className,
}: RichTextDisplayProps) {
  const extensions = useMemo(() => createExtensions(), []);
  const isEmpty = normaliseHtml(html ?? '') === '';

  const editor = useEditor({
    extensions,
    content: html ?? '',
    editable: false,
    immediatelyRender: false,
  });

  useEffect(() => {
    if (!editor) return;
    const next = html ?? '';
    if (normaliseHtml(next) === normaliseHtml(editor.getHTML())) return;
    editor.commands.setContent(next, { emitUpdate: false });
  }, [editor, html]);

  if (isEmpty) {
    return <p className={cn(richTextDisplayStyles.empty, className)}>{emptyPlaceholder}</p>;
  }

  return (
    <div className={cn(richTextDisplayStyles.root, className)}>
      <EditorContent
        editor={editor}
        className={cn(richTextProseStyles, richTextDisplayStyles.content)}
      />
    </div>
  );
}
